const trustBadgeTagName = 'jesoli-trust-badges';
const trustBadgeSelector = `${trustBadgeTagName}[data-section-id]`;

class JesoliTrustBadges extends HTMLElement {
  #abortController;
  #activeDialog;
  #closingDialog;
  #closeTimer;
  #initialized = false;
  #lastTrigger;
  #scrollState;

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.destroy();
  }

  initialize() {
    if (this.#initialized) return;

    this.#initialized = true;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    for (const trigger of this.querySelectorAll('[data-jesoli-trust-trigger]')) {
      trigger.addEventListener('click', this.#handleTriggerClick, { signal });
      trigger.addEventListener('keydown', this.#handleTriggerKeydown, { signal });
    }

    for (const dialog of this.querySelectorAll('[data-jesoli-trust-dialog]')) {
      dialog.addEventListener('click', this.#handleDialogClick, { signal });
    }

    for (const closeButton of this.querySelectorAll('[data-jesoli-trust-close]')) {
      closeButton.addEventListener('click', this.#handleCloseClick, { signal });
    }

    this.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  destroy() {
    if (!this.#initialized) return;

    this.#closeActiveDialog({ restoreFocus: false, immediate: true });
    if (this.#closingDialog instanceof HTMLElement) this.#finishClose(this.#closingDialog);
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#initialized = false;
    this.#closingDialog = undefined;
    this.#lastTrigger = undefined;
  }

  #handleTriggerClick = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLButtonElement)) return;

    this.#openDialogFromTrigger(trigger);
  };

  #handleTriggerKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLButtonElement)) return;

    this.#openDialogFromTrigger(trigger);
  };

  #handleCloseClick = () => {
    this.#closeActiveDialog({ restoreFocus: true });
  };

  #handleDialogClick = (event) => {
    if (event.currentTarget === event.target) {
      this.#closeActiveDialog({ restoreFocus: true });
    }
  };

  #handleKeydown = (event) => {
    if (!(this.#activeDialog instanceof HTMLElement)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeActiveDialog({ restoreFocus: true });
      return;
    }

    if (event.key === 'Tab') this.#trapFocus(event);
  };

  #openDialogFromTrigger(trigger) {
    const blockId = trigger.dataset.jesoliTrustTrigger;
    const dialog = this.querySelector(`[data-jesoli-trust-dialog="${blockId}"]`);
    if (!(dialog instanceof HTMLElement)) return;

    this.#openDialog(dialog, trigger);
  }

  #openDialog(dialog, trigger) {
    if (this.#activeDialog && this.#activeDialog !== dialog) {
      this.#closeActiveDialog({ restoreFocus: false, immediate: true });
    }

    if (this.#closingDialog instanceof HTMLElement) this.#finishClose(this.#closingDialog);

    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.#lastTrigger = trigger;
    this.#activeDialog = dialog;
    dialog.classList.remove('is-closing');
    dialog.inert = false;
    dialog.hidden = false;
    dialog.setAttribute('aria-hidden', 'false');
    this.#lockScroll();

    const closeButton = dialog.querySelector('[data-jesoli-trust-close]');
    if (closeButton instanceof HTMLElement) closeButton.focus();
    else dialog.focus();
  }

  #closeActiveDialog({ restoreFocus, immediate = false }) {
    if (!(this.#activeDialog instanceof HTMLElement)) {
      this.#unlockScroll();
      return;
    }

    const dialog = this.#activeDialog;
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.#activeDialog = undefined;
    this.#closingDialog = dialog;
    dialog.classList.add('is-closing');
    dialog.setAttribute('aria-hidden', 'true');
    dialog.inert = true;
    this.#unlockScroll();

    if (restoreFocus && this.#lastTrigger?.isConnected) this.#lastTrigger.focus();

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishClose(dialog);
      return;
    }

    this.#closeTimer = window.setTimeout(() => {
      this.#finishClose(dialog);
    }, 180);
  }

  #finishClose(dialog) {
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    dialog.hidden = true;
    dialog.setAttribute('aria-hidden', 'true');
    dialog.inert = true;
    dialog.classList.remove('is-closing');

    if (this.#activeDialog === dialog) this.#activeDialog = undefined;
    if (this.#closingDialog === dialog) this.#closingDialog = undefined;
  }

  #lockScroll() {
    if (this.#scrollState) return;

    const root = document.documentElement;
    const body = document.body;
    this.#scrollState = {
      bodyOverflow: body.style.overflow,
      rootOverflow: root.style.overflow,
    };
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
  }

  #unlockScroll() {
    if (!this.#scrollState) return;

    document.documentElement.style.overflow = this.#scrollState.rootOverflow;
    document.body.style.overflow = this.#scrollState.bodyOverflow;
    this.#scrollState = undefined;
  }

  #trapFocus(event) {
    const focusable = [...this.#activeDialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element instanceof HTMLElement && !element.hidden);
    if (!focusable.length) {
      event.preventDefault();
      this.#activeDialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get(trustBadgeTagName)) {
  customElements.define(trustBadgeTagName, JesoliTrustBadges);
}

const getTrustBadgeSections = (target) => {
  if (!(target instanceof Element)) return [];

  const sections = [...target.querySelectorAll(trustBadgeSelector)];
  if (target.matches(trustBadgeSelector)) sections.unshift(target);
  return sections;
};

document.addEventListener('shopify:section:load', (event) => {
  for (const section of getTrustBadgeSections(event.target)) section.initialize?.();
});

document.addEventListener('shopify:section:unload', (event) => {
  for (const section of getTrustBadgeSections(event.target)) section.destroy?.();
});
